import React from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { GetStaticPropsContext, PreviewData } from 'next';
import { ParsedUrlQuery } from 'querystring';
import Image, { StaticImageData } from 'next/image';
import { Link, Heading } from '@appsumo/dorado-react';

import { PlusBadge, SumoBadge } from '~/components/global/UserBadges';
import {
  PUBLIC_PROFILE_API_URL,
  ACCOUNT_PROFILE_URL,
  ACCOUNT_SOCIAL_BASE_URLS,
  SOCIAL_ACCOUNT_TYPES,
} from '~/constants/accounts';
import { fetchJson } from '~/lib/fetch';
import useUser from '~/lib/user';
import { formatToHumanReadableDate } from '~/lib/util';
import { AccountProfilePageProps } from '~/types/account';
import { UserSocialMedia } from '~/types/user';

import defaultProfile from '~/public/as-profile-default-avatar-dark.png';
import facebook from '~/public/brands/facebook-circle.png';
import instagram from '~/public/brands/instagram-icon.png';
import tiktok from '~/public/brands/tiktok-icon.png';
import xLogo from '~/public/brands/x-logo.png';
import linkedIn from '~/public/brands/linkedin-icon.png';
import youtube from '~/public/brands/youtube-logo.png';
import externalLink from '~/public/font-awesome/external-link-alt_bolt.png';
import verifiedUserBadge from '~/public/verified-user-badge.png';
import editIcon from '~/public/font-awesome/edit-bolt-blue.png';

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

interface IParams extends ParsedUrlQuery {
  username: string;
}

export const getStaticProps = async (
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
) => {
  const { params } = context;
  const { username } = params as IParams;

  try {
    const res = await fetchJson(`${PUBLIC_PROFILE_API_URL}${username}/`);

    return {
      props: {
        ...res,
      },
    };
  } catch (error) {
    return {
      redirect: {
        statusCode: 302,
        destination: ACCOUNT_PROFILE_URL,
      },
    };
  }
};

const socialLinks = {
  linkedin: {
    icon: linkedIn,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.LINKEDIN],
  },
  facebook: {
    icon: facebook,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.FACEBOOK],
  },
  twitter: {
    icon: xLogo,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.TWITTER],
  },
  instagram: {
    icon: instagram,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.INSTAGRAM],
  },
  tiktok: {
    icon: tiktok,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.TIKTOK],
  },
  youtube: {
    icon: youtube,
    baseURL: ACCOUNT_SOCIAL_BASE_URLS[SOCIAL_ACCOUNT_TYPES.YOUTUBE],
  },
};

function CompanyComponent({
  companyName,
  companyURL,
}: {
  companyName?: string;
  companyURL?: string;
}) {
  if (!companyName) return null;

  if (!companyURL) {
    return <p data-testid="profile-page-company">{companyName}</p>;
  }

  return (
    <Link
      href={companyURL}
      target="_blank"
      styled
      data-testid="profile-page-company"
    >
      {companyName}
    </Link>
  );
}

function EditOrReportComponent({
  isCurrentUser,
  isStaff,
}: {
  isCurrentUser: boolean;
  isStaff: boolean;
}) {
  const wrapperClasses =
    'order-2 mx-auto flex items-center lg:order-1 lg:ml-auto lg:mr-0';
  const linkPath = isCurrentUser
    ? ACCOUNT_PROFILE_URL
    : 'https://reportprofile.paperform.co/';
  const linkText = isCurrentUser ? 'Edit profile' : 'Report a problem';
  const img = isCurrentUser ? editIcon : externalLink;

  if (!isCurrentUser && isStaff) return null;

  return (
    <Link
      href={linkPath}
      className={wrapperClasses}
      styled
      data-testid="profile-page-edit-or-report"
      target={isCurrentUser ? undefined : '_blank'}
    >
      <Image src={img} alt={linkText} height={16} width={16} className="mr-2" />
      <p>{linkText}</p>
    </Link>
  );
}

export default function ProfilePage({
  username,
  avatar,
  first_name,
  last_name,
  date_joined,
  bio,
  deals_purchased,
  social_medias,
  has_plus,
  company,
  website,
  is_staff,
  email_verified,
  is_creator,
}: AccountProfilePageProps) {
  const displayName =
    first_name || last_name
      ? [first_name, last_name].join(' ').trim()
      : username;

  const displayDateJoined = formatToHumanReadableDate(date_joined, {
    month: 'short',
    day: undefined,
    year: 'numeric',
  });

  let avatarSrc: StaticImageData | string = defaultProfile;
  if (avatar) {
    if (avatar.includes('gravatar')) {
      avatarSrc = avatar;
    } else {
      avatarSrc = `${avatar}?width=256&height=256&aspect_ratio=1:1`;
    }
  }

  const { user } = useUser();
  const router = useRouter();

  return (
    <div className="mx-auto mb-24 flex max-w-screen-4xl flex-col gap-4 bg-white py-6 px-8">
      <NextSeo
        title={`${displayName} | Profile`}
        titleTemplate="%s | AppSumo"
        description={`Connect with @${displayName} on AppSumo: Explore deals, insights, and more.`}
        twitter={{
          handle: '@appsumo',
          site: '@appsumo',
          cardType: 'summary_large_image',
        }}
        openGraph={{
          type: 'website',
          siteName: 'AppSumo',
          images: [
            {
              url: 'https://appsumo2-cdn.appsumo.com/static/images/meta/appsumo-logo-meta.png',
            },
          ],
          url: router.asPath,
        }}
        noindex
        nofollow
      />
      <div className="order-1 mx-auto flex w-full max-w-xl flex-col items-center gap-y-6 lg:order-2 lg:mt-10">
        <div className="relative" data-testid="profile-page-avatar">
          <Image
            // eslint-disable-next-line tailwindcss/enforces-shorthand
            className="h-32 w-32 rounded-full border-2 border-white shadow-lg"
            unoptimized
            src={avatarSrc}
            alt="Profile"
            width={128}
            height={128}
          />
          {email_verified && (
            <span
              className="absolute bottom-0 right-3 rounded-full p-1"
              data-testid="profile-page-verified"
            >
              <Image
                src={verifiedUserBadge}
                alt="Verified user"
                height={20}
                width={20}
              />
            </span>
          )}
        </div>
        <div className="flex w-full flex-col items-center">
          <div
            className="flex flex-row items-center"
            data-testid="profile-page-name"
          >
            <Heading.H2 className="font-semibold text-gray-900">
              {displayName}
            </Heading.H2>
            {(has_plus || is_staff) && (
              <div className="ml-2 flex flex-row gap-x-2">
                {has_plus && <PlusBadge />}
                {is_staff && <SumoBadge />}
              </div>
            )}
          </div>
          {!!bio && (
            <p className="text-center" data-testid="profile-page-bio">
              {bio}
            </p>
          )}
          <CompanyComponent companyName={company} companyURL={website} />

          <div className="my-2 w-full border-t border-gray-300"></div>
          <div
            className="text-sm text-gray-600"
            data-testid="profile-page-user-stats"
          >
            <span>Member since: {displayDateJoined}</span>
            {!(is_creator || is_staff) && (
              <span className="ml-1">
                &bull; Deals bought: {deals_purchased}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex flex-row gap-6"
          data-testid="profile-page-social-links"
        >
          {!!social_medias &&
            Object.entries(socialLinks).map(([key, value]) => {
              const socialUsername =
                social_medias[`${key}_username` as keyof UserSocialMedia];
              if (socialUsername) {
                return (
                  <Link
                    key={key}
                    href={`${value.baseURL}${socialUsername}`}
                    data-testid={`profile-page-${key}-link`}
                    target="_blank"
                  >
                    <Image src={value.icon} alt={key} height={20} width={20} />
                  </Link>
                );
              }
            })}
        </div>
      </div>

      <EditOrReportComponent
        isCurrentUser={user?.username === username}
        isStaff={is_staff}
      />
    </div>
  );
}
